import React from "react";
import "./SharedStyles.scss";
import { IoMdAlert, IoMdCheckmarkCircle, IoMdWarning } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { filterApiErrorMessages } from "../../utils/apiErrorMessages";

interface IProps {
  message?: string;
  messages?: string[];
  type: "error" | "warning" | "success" | "gray";
  showOnlyIcon?: boolean;
  compact?: boolean;
  variant?: "default" | "chip";
  dangerouslySetInnerHTML?: boolean;
  buttonText?: string;
  onButtonClick?: () => void;
}

const EAAlert: React.FC<IProps> = ({
  message,
  messages,
  type,
  showOnlyIcon = false,
  compact = false,
  variant = "default",
  dangerouslySetInnerHTML = false,
  buttonText,
  onButtonClick,
}) => {
  const { t } = useTranslation();
  const getIcon = (type: "success" | "error" | "warning" | "gray") => {
    switch (type) {
      case "success":
        return <IoMdCheckmarkCircle />;
      case "error":
        return <IoMdAlert />;
      case "warning":
        return <IoMdWarning />;
      case "gray":
        return <IoMdCheckmarkCircle />;
      default:
        return null;
    }
  };

  const icon = getIcon(type);

  const rawMessages = messages ?? (message ? [message] : []);
  const cleanMessages: string[] = type === "error" ? filterApiErrorMessages(rawMessages) : rawMessages.filter(Boolean);

  const alertClasses = `ea-alert ea-alert--${type} ${showOnlyIcon ? "ea-alert__only-icon" : ""} ${
    compact ? "ea-alert--compact" : ""
  } ${variant === "chip" ? "ea-alert--chip" : ""}`.trim();

  if (!showOnlyIcon && cleanMessages.length === 0) {
    return null;
  }

  if (showOnlyIcon || cleanMessages.length === 0) {
    return (
      <div className={alertClasses}>
        <span className="ea-alert__icon">{icon}</span>
      </div>
    );
  }

  return (
    <div className={alertClasses}>
      <span className="ea-alert__icon">{icon}</span>
      {dangerouslySetInnerHTML ? (
        <span className="ea-alert__message" dangerouslySetInnerHTML={{ __html: cleanMessages.join(" ") }} />
      ) : (
        <span className="ea-alert__message">
          {cleanMessages.length === 1 ? (
            cleanMessages[0]
          ) : (
            <span className="ea-alert__message-list">
              {cleanMessages.map((msg, i) => (
                <span key={i} className="ea-alert__message-item">
                  {t(msg)}
                </span>
              ))}
            </span>
          )}
          {buttonText && onButtonClick && (
            <span onClick={onButtonClick} className={"ea-alert__button"}>
              {buttonText}
            </span>
          )}
        </span>
      )}
    </div>
  );
};

export default EAAlert;
